// @ts-nocheck
/**
 * Agent Activity list — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + Card filter row + clean shadcn Table + lucide-icon pagination).
 * Server-paginated: filters trigger fetchData; PAGE_SIZE fixed at 25.
 * ActivityTimeline modal preserved.
 */
import { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import moment from 'moment';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Activity as ActivityIcon,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_ACTIVITY_LOG_LIST, API_AGENTS_LIST } from '@/service/url';
import ActivityTimeline from '@/features/agent-activity/ActivityTimeline';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const defaultStart = () => moment().startOf('month').format('YYYY-MM-DD');
const defaultEnd = () => moment().format('YYYY-MM-DD');

const PAGE_SIZE = 25;

const fmtDateTime = (v: any) => (v ? moment(v).format('YYYY-MM-DD HH:mm:ss') : '—');

const AgentActivityList = () => {
  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate] = useState(defaultEnd());
  const [agentId, setAgentId] = useState<any>(null);

  const [agents, setAgents] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [detailRow, setDetailRow] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp: any = await getApiCall(API_AGENTS_LIST, { fields: 'min' });
        const list = Array.isArray(resp) ? resp : resp?.rows || resp?.data || [];
        const opts = list
          .map((a: any) => ({
            label:
              `${a.agent_name || a.name || 'Agent'}` +
              (a.agent_number ? ` (${a.agent_number})` : ''),
            value: a.id,
          }))
          .filter((o: any) => o.value != null);
        setAgents([{ label: 'All Agents', value: null }, ...opts]);
      } catch (e) {
        setAgents([{ label: 'All Agents', value: null }]);
      }
    })();
  }, []);

  const fetchData = useCallback(
    async (targetPage = 1) => {
      if (!startDate || !endDate) {
        toast.error('Select both start and end dates');
        return;
      }
      if (moment(startDate).isAfter(moment(endDate))) {
        toast.error('Start date cannot be after end date');
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          page: String(targetPage),
          limit: String(PAGE_SIZE),
        });
        if (agentId) params.append('agent_id', String(agentId));

        const resp: any = await getApiCall(
          `${API_AGENT_ACTIVITY_LOG_LIST}?${params.toString()}`,
          {},
        );
        setRows(Array.isArray(resp?.rows) ? resp.rows : []);
        setTotal(Number(resp?.total) || 0);
        setPage(Number(resp?.page) || targetPage);
      } catch (e: any) {
        toast.error(friendlyApiError(e) || 'Failed to load activity logs');
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, agentId],
  );

  useEffect(() => {
    fetchData(1);
  }, [agentId]);

  const selectedAgentOption =
    agents.find((o: any) => o.value === agentId) || (agents.length ? agents[0] : null);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <FillPage>
      <PageHeader
        title="Agent Activity"
        description="Daily login/logout and per-agent activity events, filterable by date range."
      />

      {/* Filters */}
      <div
        className="mb-3 rounded-xl border border-border/60 bg-card px-3 py-2.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-0.5">
            <Label htmlFor="start_date" className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Start Date
            </Label>
            <CalendarDateInput
              id="start_date"
              value={startDate}
              onChange={(e) => setStartDate(e)}
              max={endDate}
              className="input-embossed h-9 w-[150px] rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="space-y-0.5">
            <Label htmlFor="end_date" className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              End Date
            </Label>
            <CalendarDateInput
              id="end_date"
              value={endDate}
              onChange={(e) => setEndDate(e)}
              min={startDate}
              max={defaultEnd()}
              className="input-embossed h-9 w-[150px] rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="min-w-[220px] flex-1 space-y-0.5">
            <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Agent
            </Label>
            <Select
              value={selectedAgentOption}
              options={agents}
              onChange={(opt: any) => setAgentId(opt?.value ?? null)}
              placeholder="All Agents"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                control: (base: any, state: any) => ({
                  ...base,
                  minHeight: 36,
                  height: 36,
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid hsl(var(--border) / 0.6)',
                  backgroundColor: 'hsl(var(--card))',
                  boxShadow: state.isFocused
                    ? 'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9), 0 0 0 3px hsl(var(--ring) / 0.15)'
                    : 'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    borderColor: 'hsl(var(--border))',
                  },
                }),
                valueContainer: (base: any) => ({
                  ...base,
                  padding: '0 8px',
                  height: 34,
                }),
                input: (base: any) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                  color: 'hsl(var(--foreground))',
                }),
                singleValue: (base: any) => ({
                  ...base,
                  color: 'hsl(var(--foreground))',
                }),
                placeholder: (base: any) => ({
                  ...base,
                  color: 'hsl(var(--muted-foreground))',
                }),
                indicatorSeparator: () => ({ display: 'none' }),
                dropdownIndicator: (base: any) => ({
                  ...base,
                  padding: 6,
                  color: 'hsl(var(--muted-foreground))',
                }),
                menu: (base: any) => ({
                  ...base,
                  borderRadius: 8,
                  border: '1px solid hsl(var(--border) / 0.6)',
                  backgroundColor: 'hsl(var(--card))',
                  color: 'hsl(var(--foreground))',
                  boxShadow:
                    '10px 10px 24px rgba(163, 177, 198, 0.4), -10px -10px 24px rgba(255, 255, 255, 0.9)',
                  overflow: 'hidden',
                }),
                option: (base: any, state: any) => ({
                  ...base,
                  fontSize: 12,
                  padding: '8px 12px',
                  backgroundColor: state.isSelected
                    ? 'hsl(var(--primary) / 0.15)'
                    : state.isFocused
                      ? 'hsl(var(--primary) / 0.08)'
                      : 'transparent',
                  color: state.isSelected
                    ? 'hsl(var(--primary))'
                    : 'hsl(var(--foreground))',
                  cursor: 'pointer',
                }),
              }}
            />
          </div>
          <Button size="sm" onClick={() => fetchData(1)} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-primary/[0.06]">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Agent
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Date
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Login
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Activities
                    </th>
                    <th className="w-24 px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-10 text-center text-sm text-muted-foreground"
                      >
                        No activity found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row: any, idx: number) => {
                      const a = row.agent || {};
                      const count =
                        row.activity_count ??
                        (Array.isArray(row.activity) ? row.activity.length : 0);
                      const isStripe = idx % 2 === 1;
                      return (
                        <tr
                          key={row.id}
                          className={
                            'border-t border-border/50 transition-colors hover:bg-primary/[0.06] ' +
                            (isStripe ? 'bg-muted/30' : 'bg-card')
                          }
                        >
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-foreground">
                              {a.agent_name || `Agent #${row.agent_id}`}
                            </div>
                            {a.agent_number ? (
                              <div className="text-xs text-muted-foreground">
                                {a.agent_number}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground">
                            {row.date || '—'}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-foreground">
                            {fmtDateTime(row.login_time)}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary ring-1 ring-inset ring-primary/25 tabular-nums">
                              {count}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              type="button"
                              onClick={() => setDetailRow(row)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                              title="View timeline"
                              aria-label="View timeline"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {total > PAGE_SIZE && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-1 pb-2 pt-3 text-xs text-muted-foreground">
              <span>
                Page{' '}
                <span className="font-medium text-foreground">{page}</span> of{' '}
                <span className="font-medium text-foreground">{totalPages}</span>
                {' · '}
                <span className="font-medium text-foreground">{total}</span> total
              </span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(1)}
                  disabled={page <= 1}
                  className="gap-1.5"
                  aria-label="First page"
                >
                  <ChevronsLeft className="h-3.5 w-3.5" />
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page - 1)}
                  disabled={page <= 1}
                  className="gap-1.5"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page + 1)}
                  disabled={page >= totalPages}
                  className="gap-1.5"
                  aria-label="Next page"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(totalPages)}
                  disabled={page >= totalPages}
                  className="gap-1.5"
                  aria-label="Last page"
                >
                  Last
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Activity timeline modal */}
      <Dialog open={!!detailRow} onOpenChange={(o) => !o && setDetailRow(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <ActivityIcon className="h-5 w-5" strokeWidth={2.3} />
              </span>
              <div>
                <DialogTitle className="text-base">
                  {detailRow?.agent?.agent_name || `Agent #${detailRow?.agent_id}`}
                </DialogTitle>
                <div className="text-xs text-muted-foreground">
                  Activity timeline · {detailRow?.date || '—'}
                </div>
              </div>
            </div>
          </DialogHeader>
          <ActivityTimeline events={detailRow?.activity} />
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default AgentActivityList;
